import { h } from "preact";
import { useState, useEffect, useRef } from "preact/hooks";

import { Keyboard } from "../../../../foundation/enums";
import classname from "../../../../foundation/utils/classname";
import { stopPropagation } from "../../../../foundation/utils/legacy";
import HighlightText, { HighlightTextProps } from "../../HighlightText";
import Input, { InputProps } from "../Input";

import styles from "./RichInput.module.css";

type InheritedInputProps = Omit<InputProps, "value" | "onChange" | "onBlur">;
type InheritedHighlightTextProps = Omit<HighlightTextProps, "text">;

interface Props<T extends string | number> extends InheritedInputProps, InheritedHighlightTextProps {
    value: T;
    disabled?: boolean;
    required?: boolean;
    trim?: boolean;
    blurOnEnter?: boolean;
    resetOnEscape?: boolean;
    disableOnHighlight?: boolean;
    formatter?: (value: T) => T;
    onChange?: (value: T) => void;
    onInput?: (ev: Event) => void;
    onFocus?: (ev: Event) => void;
    onBlur?: (ev: Event, args: BlurData<T>) => void;
    onKeyDown?: (ev: KeyboardEvent) => void;
}

interface BlurData<T = string | number> {
    value: T;
    discard: boolean;
}

function RichInput<T extends string | number>({
    class: cssClass,
    inputClass,
    textClass: textClassProp,
    value: valueProp,
    placeholder,
    variant,
    icon,
    required = true,
    disabled = false,
    inputRef: inputRefProp,
    formatter,
    // Flags
    trim = true,
    blurOnEnter = true,
    resetOnEscape = true,
    disableOnHighlight = true,
    // Callbacks
    onChange,
    onInput,
    onFocus,
    onBlur,
    onKeyDown,
    // HighlightTextProps
    filterValue,
    caseInsensitive,
    enableWordSearch,
    excludeZeroScore,
    completeMatch,
    highlightClass,
    ...inputProps
}: Props<T>) {
    const [value, setValue] = useState(valueProp);
    const [focusWithin, setFocusWithin] = useState(false);
    const [discard, setDiscard] = useState(false);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const formattedTextRef = useRef<HTMLDivElement | null>(null);

    const showHighlightText = !!filterValue && !focusWithin;

    const formattedValue = formatter?.(value);
    const showFormattedText = !!formatter && formattedValue !== value && !focusWithin;

    const textClass = textClassProp ?? "body-1";

    function isDisabled(): boolean {
        return disabled || (showHighlightText && disableOnHighlight);
    }

    function handleKeyDown(ev: KeyboardEvent) {
        // Prevent triggering keydown event twice on keys like Enter, Escape, Arrow keys etc.
        // Similar issue: https://github.com/vuejs/vue/issues/10277
        // Explanation for the 229 keyCode: https://lists.w3.org/Archives/Public/www-dom/2010JulSep/att-0182/keyCode-spec.html
        if (ev.isComposing || ev.keyCode === 229) {
            return;
        }

        switch (ev.key) {
            case Keyboard.Key.Enter:
                if (blurOnEnter) {
                    inputRef.current!.blur();
                }

                break;

            case Keyboard.Key.Escape:
                if (resetOnEscape) {
                    setValue(valueProp);
                    setDiscard(true);
                    setTimeout(() => {
                        inputRef.current!.blur();
                    });
                }

                break;

            default:
                // no-op
        }

        onKeyDown?.(ev);
    }

    function handleInput(ev: Event) {
        const {
            type,
            value: inputValue,
            min,
            max,
            validity
        } = inputRef.current!;

        let newValue;

        if (validity.rangeUnderflow) {
            newValue = min;
        } else if (validity.rangeOverflow) {
            newValue = max;
        } else if (validity.badInput) {
            newValue = valueProp;
        } else if (validity.valueMissing) {
            newValue = "";
        } else if (validity.badInput || validity.patternMismatch) {
            newValue = valueProp;
        } else {
            newValue = type === "number"
                ? String(parseInt(inputValue, 10))
                : inputValue;
        }

        setValue(newValue as T);
        onInput?.(ev);
    }

    function handleFocus(ev: Event) {
        setFocusWithin(true);
        setDiscard(false);
        onFocus?.(ev);
    }

    function handleBlur(ev: Event) {
        if (!inputRef.current) {
            return;
        }

        const { type, value: inputValue, valueAsNumber } = inputRef.current!;
        const valueAsString = trim ? inputValue.trim() : inputValue;
        const newValue = (type === "number" ? valueAsNumber : valueAsString) as T;

        if (required && value === "") {
            setValue(valueProp);
            onChange?.(valueProp);
        } else {
            setValue(newValue);
            onChange?.(newValue);
        }

        setFocusWithin(false);
        onBlur?.(ev, { value: newValue, discard });
    }

    function handleInputRef(el: HTMLInputElement | null) {
        inputRef.current = el;

        if (inputRefProp) {
            if (typeof inputRefProp === "function") {
                inputRefProp(el);
            } else {
                inputRefProp.current = el;
            }
        }
    }

    function handleFormattedTextMouseDown(ev: MouseEvent) {
        ev.stopPropagation();
    }

    function renderMirrorContent() {
        if (showHighlightText) {
            return (
                <HighlightText
                    class={classname(textClass, styles.highlightText)}
                    text={value as string}
                    filterValue={filterValue}
                    caseInsensitive={caseInsensitive}
                    enableWordSearch={enableWordSearch}
                    excludeZeroScore={excludeZeroScore}
                    completeMatch={completeMatch}
                    highlightClass={highlightClass}
                    onMouseDown={stopPropagation}/>
            );
        }

        if (showFormattedText) {
            return (
                <div
                    class={classname(textClass, styles.formattedText)}
                    dangerouslySetInnerHTML={{
                        __html: formattedValue as string
                    }}
                    ref={formattedTextRef}
                    onMouseDown={handleFormattedTextMouseDown}>
                </div>
            );
        }

        return null;
    }

    useEffect(() => {
        if (valueProp !== value) {
            setValue(valueProp);
        }
    }, [valueProp]);

    return (
        <Input
            class={classname(styles.richInput, cssClass, {
                [styles.disabled]: isDisabled(),
                [styles.hasHighlightText]: showHighlightText,
                [styles.hasFormattedText]: showFormattedText
            })}
            inputClass={classname(styles.input, inputClass)}
            textClass={textClass}
            value={value}
            placeholder={placeholder}
            variant={variant}
            icon={icon}
            disabled={isDisabled()}
            required={required}
            mirrorContent={renderMirrorContent()}
            inputRef={handleInputRef}
            onInput={handleInput}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onMouseDown={stopPropagation}
            {...inputProps}/>
    );
}

export default RichInput;
export {
    Props as RichInputProps,
    BlurData as RichInputBlurData
};
